import contactService from '../services/contactService';
import { sha256Hex } from './sha256';
import { cleanNumber } from './smsCompose';

/**
 * Contacts sync without a contact upload (sign-up v3 CONTACTS step, INVITE FRIENDS — Oct 2026).
 *
 * The address book never leaves the phone. Each number is put into E.164 (`toE164`), hashed
 * (SHA-256, lowercase hex — the same rule as `ContactMatchService.HashNumber` on the server) and
 * ONLY the hashes go to `POST api/contacts/match`, which answers with the accounts whose own
 * number hashes the same. Names, labels and the numbers themselves stay here, paired back to the
 * answer by hash.
 */

/** The country a bare ten-digit number is assumed to be in. Every current user is in +1. */
export const DEFAULT_DIAL = '+1';

/**
 * A contact's number → E.164, or '' when it cannot be one. Mirrors `Utils/PhoneNumbers.cs`:
 * a `+` is believed; `011` / `00` are international prefixes; eleven digits starting with 1 are
 * NANP with the trunk code; ten digits take the default country; anything else is not a phone.
 */
export function toE164(raw, defaultDial = DEFAULT_DIAL) {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (s.startsWith('011')) s = `+${s.slice(3)}`;
  else if (s.startsWith('00')) s = `+${s.slice(2)}`;
  const plus = s.startsWith('+');
  const digits = s.replace(/\D/g, '');
  if (!digits) return '';
  let candidate;
  if (plus) candidate = `+${digits}`;
  else if (digits.length === 11 && digits.startsWith('1')) candidate = `+${digits}`;
  else if (digits.length === 10) candidate = `${defaultDial}${digits}`;
  else return '';
  if (!/^\+[1-9]\d{6,14}$/.test(candidate)) return '';
  // Inside +1 the plan is known: NXX NXX XXXX, both N in 2–9.
  if (candidate.startsWith('+1') && !/^\+1[2-9]\d{2}[2-9]\d{6}$/.test(candidate)) return '';
  return candidate;
}

/** `250 ··· 0143` — enough to recognise which number, nothing a shoulder can read. */
export function maskNumber(e164OrRaw) {
  const d = String(e164OrRaw || '').replace(/\D/g, '');
  if (d.length < 7) return d;
  const national = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return `${national.slice(0, 3)} ··· ${national.slice(-4)}`;
}

/**
 * Every usable number across the book, one row per distinct E.164:
 * `[{ contactId, name, number, e164, hash }]`. Hashing is async (subtle when the page has it).
 */
export async function hashContacts(people) {
  const rows = [];
  const seen = new Set();
  for (const c of people || []) {
    for (const p of c.phones || []) {
      const e164 = toE164(p.number);
      if (!e164 || seen.has(e164)) continue;
      seen.add(e164);
      rows.push({ contactId: c.id, name: c.name, number: cleanNumber(p.number) || p.number, e164, hash: await sha256Hex(e164) }); // eslint-disable-line no-await-in-loop
    }
  }
  return rows;
}

/**
 * The CONTACTS step's answer: `{ checked, matches: [{ user, contact }], others: [contact] }`.
 * `matches` are contacts who already have a Portal account (one per account, with how they
 * already relate to you); `others` are the rest, one row per contact (their first usable
 * number), which is who WHO SHOULD BE HERE picks three from.
 */
export async function matchContacts(people) {
  const rows = await hashContacts(people);
  let matches = [];
  if (rows.length) {
    const r = await contactService.match(rows.map((x) => x.hash));
    if (!r.success) throw new Error(r.error || 'Could not check your contacts.');
    const byHash = new Map(rows.map((x) => [x.hash, x]));
    const seenUsers = new Set();
    matches = (r.data?.matches || [])
      .filter((m) => byHash.has(m.hash) && !seenUsers.has(m.userId) && seenUsers.add(m.userId))
      .map((m) => ({ user: m, contact: byHash.get(m.hash) }));
  }
  const matchedContactIds = new Set(matches.map((m) => m.contact.contactId));
  const seenContacts = new Set();
  const others = rows.filter((x) => !matchedContactIds.has(x.contactId) && !seenContacts.has(x.contactId) && seenContacts.add(x.contactId));
  return { checked: rows.length, matches, others };
}
